import { LEGACY_BARCODE_TO_SKU_CONFIG } from 'config/legacyBarcodeToSkuConfig';

function normalizePackType(p) {
  if (p == null || p === '') return '1';
  return String(p);
}

/**
 * @param {object} config — entry from LEGACY_BARCODE_TO_SKU_CONFIG
 * @param {object} sku — product_sku row (camelCase or snake_case)
 */
function configMatchesSku(config, sku) {
  const ct = sku.containerType ?? sku.container_type;
  const ut = sku.unitType ?? sku.unit_type;
  const pt = normalizePackType(sku.packType ?? sku.pack_type);
  const cs = sku.caseType ?? sku.case_type;

  if (ct !== config.containerType) return false;
  if (String(ut) !== String(config.unitType)) return false;
  if (normalizePackType(config.packType) !== pt) return false;
  if (config.caseType) {
    if (cs && cs !== config.caseType) return false;
  }
  return true;
}

/**
 * Parse barcode_slab|case|keg.{unitSize} → packaging family + unitSize string for forms.
 * @param {string} legacyKey e.g. barcode_slab.24x.440
 * @returns {{ packagingType: 'slab'|'case'|'keg', unitSize: string }|null}
 */
export function parsePackagingLegacyKey(legacyKey) {
  const m = legacyKey.match(/^barcode_(slab|case|keg)\.(.+)$/);
  if (!m) return null;
  return { packagingType: m[1], unitSize: m[2] };
}

function humanLabelFor(packagingType, unitSize) {
  const tier = packagingType.charAt(0).toUpperCase() + packagingType.slice(1);
  return `${tier} — ${unitSize}`;
}

/**
 * Infer slab / case / keg label type and unit size from SKU by reverse-matching legacy config.
 *
 * @param {object} sku
 * @returns {{
 *   packagingType: 'slab'|'case'|'keg',
 *   unitSize: string,
 *   legacyKey: string,
 *   humanLabel: string,
 * } | {
 *   ambiguous: true,
 *   options: Array<{ packagingType, unitSize, legacyKey, humanLabel }>,
 * } | null}
 */
export function inferLabelFromSku(sku) {
  if (!sku) return null;

  const keys = Object.keys(LEGACY_BARCODE_TO_SKU_CONFIG).filter((k) => (
    k.startsWith('barcode_slab.')
    || k.startsWith('barcode_case.')
    || k.startsWith('barcode_keg.')
  ));

  const matchedKeys = keys.filter((k) => {
    const config = LEGACY_BARCODE_TO_SKU_CONFIG[k];
    return configMatchesSku(config, sku);
  });

  if (!matchedKeys.length) return null;

  const skuCase = sku.caseType ?? sku.case_type;

  matchedKeys.sort((a, b) => {
    const ca = LEGACY_BARCODE_TO_SKU_CONFIG[a];
    const cb = LEGACY_BARCODE_TO_SKU_CONFIG[b];
    const score = (cfg) => (skuCase && cfg.caseType === skuCase ? 1 : 0);
    const d = score(cb) - score(ca);
    if (d !== 0) return d;
    return a.localeCompare(b);
  });

  const options = [];
  const seen = new Set();
  matchedKeys.forEach((legacyKey) => {
    const parsed = parsePackagingLegacyKey(legacyKey);
    if (!parsed) return;
    const dedupeId = `${parsed.packagingType}|${parsed.unitSize}`;
    if (seen.has(dedupeId)) return;
    seen.add(dedupeId);
    options.push({
      packagingType: parsed.packagingType,
      unitSize: parsed.unitSize,
      legacyKey,
      humanLabel: humanLabelFor(parsed.packagingType, parsed.unitSize),
    });
  });

  if (!options.length) return null;
  if (options.length === 1) return options[0];
  return { ambiguous: true, options };
}
